import React, { useEffect, useState } from 'react';
import Layout from '../../components/layout/Layout';
import { useNavigate } from "react-router-dom";
import 'bootstrap/dist/css/bootstrap.min.css';
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography,
    Button, TextField, Select, MenuItem, Pagination, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Progress from '../../hooks/useProgressBar';
import useDebounce from '../../hooks/useDebounce';
import { getData, putData } from '../../services/apiService';
import useFetchIp from "../../hooks/useFetchIp";

const AdminSupportTicket = () => {
    const { ip } = useFetchIp();
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearchQuery = useDebounce(searchQuery, 500);
    const [totalPages, setTotalPages] = useState(1);

    // Fetch on load
    useEffect(() => {
        fetchData();
    }, [page, rowsPerPage, debouncedSearchQuery]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await getData(`/get-all-tickets?page=${page}&limit=${rowsPerPage}&search=${debouncedSearchQuery}`);
            if (response) {
                setData(response.data);
                setTotalPages(response.totalPages);
            }
            setLoading(false);
        } catch (error) {
            console.error('Failed to fetch data', error);
            setLoading(false);
        }
    }

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setPage(1);
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(event.target.value);
        setPage(1);
    };

    const handleCloseTicket = async (id) => {
        try {
            await putData(`/close-ticket/${id}`, { ipAddress: ip});
            fetchData();
        } catch (error) {
            console.error('Failed to close ticket', error);
        }
    };

    const handleStatusChange = async (id, status) => {
        try {
            const updateData = {
                status: status,
            };
            await putData(`/change-status/${id}`, updateData);
            fetchData();
        } catch (error) {
            console.error('Failed to close ticket', error);
        }
    };

    return (
        <Layout>
            {loading ? <Progress /> :
                <Box className="p-6 bg-gray-100 min-h-screen">
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', mb: 3 }}>Support Tickets</Typography>
                    {/* Header: Add Button (left) and Search (right) */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2, }} >
                        <TextField placeholder="Search Lession" value={searchQuery} onChange={handleSearchChange} variant="outlined" size="medium"
                            sx={{ width: 350, bgcolor: '#e9ecef', borderRadius: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, }, }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#757575' }} />
                                    </InputAdornment>
                                ),
                                sx: { bgcolor: '#e9ecef', borderRadius: 2 },
                            }}
                        />
                    </Box>
                    {/* <TableContainer component={Paper} elevation={0} sx={{ mb: 2, borderRadius: 2, boxShadow: 2, overflow: 'hidden', }} > */}

                    <TableContainer component={Paper}
                        sx={{
                            width: "100%",
                            maxWidth: "100%",
                            overflowX: "auto",
                            overflowY: "hidden",
                            "&::-webkit-scrollbar": {
                                height: "8px"
                            },
                            "&::-webkit-scrollbar-thumb": {
                                backgroundColor: "#888",
                                borderRadius: "4px"
                            }
                        }}
                    >
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#4B49AC' }}>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600, borderTopLeftRadius: 8 }}> ID </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Ticket No </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Topic </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Description </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Page url </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Status </TableCell>
                                    {/* <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Ticket Image </TableCell> */}
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600, borderTopRightRadius: 8 }}> Action </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.map((item, index) => (
                                    <TableRow key={item._id}>
                                        <TableCell align="center">
                                            <Typography variant="body1">{(page - 1) * rowsPerPage + index + 1}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.support_ticket_no}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.title}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.message}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.page_url}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Select
                                                disabled={item.status === "RESOLVED"}
                                                value={item.status}
                                                onChange={(e) => handleStatusChange(item._id, e.target.value)}
                                                size="small"
                                                sx={{
                                                    color: "white",
                                                    borderradius: "8px",
                                                    backgroundColor:
                                                        item.status === "OPEN"
                                                            ? "#ff9007"
                                                            : item.status === "IN-REVIEW"
                                                                ? "green"
                                                                : item.status === "RESOLVED"
                                                                    ? "rgb(215 212 212)"
                                                                    : "gray",
                                                    "& .MuiSelect-icon": {
                                                        color: item.status === "RESOLVED" ? "gray" : "white",
                                                    },
                                                }}
                                            >
                                                <MenuItem value="OPEN">OPEN</MenuItem>
                                                <MenuItem value="IN-REVIEW">IN-REVIEW</MenuItem>
                                                <MenuItem value="RESOLVED">RESOLVED</MenuItem>
                                            </Select>
                                        </TableCell>



                                        {/* <TableCell align="center">
                                            <Typography variant="body1"><img src={item.ticket_image} alt="" className="img-fluid" style={{ width: "60px", height: "70px" }} /></Typography>
                                        </TableCell> */}
                                        <TableCell align="center">
                                            <Button variant="contained" color="success" size="small" onClick={() => handleCloseTicket(item._id)} disabled={!item.isActive} sx={{ mr: 1 }} >
                                                Close Ticket
                                            </Button>

                                            <Button variant="contained" color="success" size="small" onClick={() => navigate("/ticket-details?id=" + item._id)} >
                                                View Ticket
                                            </Button>
                                        </TableCell>

                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {/* Pagination */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <Pagination count={totalPages} page={page} onChange={handlePageChange} shape="rounded"
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    '&.Mui-selected': {
                                        bgcolor: '#4B49AC',
                                        color: 'white',
                                    },
                                },
                            }}
                        />
                        <Select value={rowsPerPage} onChange={handleRowsPerPageChange} size="small"
                            sx={{ ml: 2, height: 35, bgcolor: '#f7f7f7', border: '1px solid #e2e2e2', '& .MuiSelect-select': { py: 0.75 }, }}
                            IconComponent={KeyboardArrowDownIcon} >
                            <MenuItem value={10}>10 / page</MenuItem>
                            <MenuItem value={20}>20 / page</MenuItem>
                            <MenuItem value={50}>50 / page</MenuItem>
                        </Select>
                    </Box>

                </Box>
            }
        </Layout>
    );
};

export default AdminSupportTicket;